<template>
  <el-select
    v-model="value"
    :disabled="disabled"
    :multiple="multiple"
    clearable
    filterable
    collapse-tags
    popper-class="custom-header"
    :max-collapse-tags="1"
    :filter-method="filterMethod"
    @update:modelValue="update"
  >
    <template #header>
      <el-checkbox v-model="checkAll" @change="handleCheckAll">
        All
      </el-checkbox>
    </template>
    <el-option
      v-for="item in filteredItems"
      :key="(item as any).id"
      :label="getItemLabel(item)"
      :value="(item as any).id"
    >
      {{ getItemLabel(item) }}
    </el-option>
  </el-select>
</template>

<script lang="ts">
import { defineComponent, watch, ref, computed } from 'vue'

export default defineComponent({
  name: 'CreativeSelect',
  props: {
    modelValue: {
      required: true,
      type: [String, Array]
    },
    items: {
      type: Array,
      required: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    multiple: {
      type: Boolean,
      default: false
    }
  },
  setup(props, context) {
    const value = computed(() => props.modelValue)
    const defaultProps = { ...props }
    const checkAll = ref(false)
    const filterQuery = ref('')

    const getItemLabel = (item: any) =>
      `ID: ${item.id} - ${item.attributes.name}`

    const filteredItems = computed(() => {
      if (!filterQuery.value) {
        return props.items
      }
      return props.items.filter((item: any) => {
        return getItemLabel(item)
          .toLowerCase()
          .includes(filterQuery.value.toLowerCase())
      })
    })

    const update = function (newValue: any) {
      if (props.multiple) {
        context.emit(
          'update:modelValue',
          newValue.map((x: any) => String(x))
        )
      } else {
        context.emit('update:modelValue', String(newValue))
      }
    }

    const handleCheckAll = (val: any) => {
      if (!props.multiple) return

      const currentValues = Array.isArray(value.value)
        ? value.value.map((x: any) => String(x))
        : []
      const filteredIds = filteredItems.value.map((item: any) =>
        String(item.id)
      )

      if (val) {
        const newValues = [...new Set([...currentValues, ...filteredIds])]
        update(newValues)
      } else {
        const newValues = currentValues.filter(
          (id: string) => !filteredIds.includes(id)
        )
        update(newValues)
      }
    }

    const filterMethod = (query: string) => {
      filterQuery.value = query
      return true
    }

    watch(
      (_: any) => props.items,
      (val: any) => {
        const values = Array.isArray(value.value) ? value.value : [value.value]
        const newIds = val.map((x: any) => x.id)
        const idExist = values.some((item) => newIds.includes(item))
        if (!idExist) {
          update(defaultProps.modelValue)
        }
      }
    )

    return {
      update,
      value,
      handleCheckAll,
      checkAll,
      filteredItems,
      filterMethod,
      getItemLabel
    }
  }
})
</script>
